/** Keeping the working call's answer form and its saves in step.
 *
 *  `CallAnswers.tsx` saves each answer as it is given, through a queue, while
 *  the dispatcher goes on answering. A save is slow and he is not, so when a
 *  reply lands the form may have moved on, and the next save in the queue was
 *  made before the reply existed. Every rule that decided what to show and
 *  what to send by looking at VALUES lost something -- three review rounds of
 *  the vehicle question found a different loss in each:
 *
 *  - "The reply wins for the keys the save named" wiped a Model being typed
 *    while Make saved: a vehicle is four boxes on one key.
 *  - "The reply wins only where the form still holds what was sent" brought
 *    back an answer cleared mid-save -- a cleared answer is absent, and absent
 *    never matches -- and let a rolled-back value outvote a later save that
 *    had stored the new one.
 *  - "The form goes back on failure" took answers pressed after the failed
 *    save was made. And a save queued behind a REFUSED one sent the refused
 *    value again, so the answer pressed in the same motion was refused with it.
 *
 *  So this decides by WHEN, which a value cannot tell it. Every change made by
 *  hand bumps a counter and records, per answer, the count it was made at. A
 *  save records the count it was made at. Then:
 *
 *  - SHOWING a reply, or a failure: each answer takes the server's view unless
 *    it was changed by hand after the save was made, in which case the form's
 *    value -- present or absent -- is newer than anything the reply can know,
 *    and its own save will send it. A rollback is not a change by hand, so it
 *    never outvotes a save made after it.
 *  - SENDING a save: only answers changed by hand since the last save whose
 *    outcome has been SETTLED. What an earlier save already carried was either
 *    stored, or refused -- and a refused value is not sent again unless it is
 *    given again. A save that failed for a reason that is not a refusal (the
 *    network, a server restarting) settles nothing: what it carried was not
 *    judged at all, so the next save carries it again.
 *
 *  NO REACT IN HERE, deliberately, so the rules can be run as plain code:
 *  `web/scripts/answer-sync-scenarios.mjs` replays every loss above against
 *  this class and asserts it no longer happens. Run it after touching this.
 */

export type Answers = Record<string, string>

export class AnswerSync {
  /** The form as last drawn. */
  form: Answers
  /** What the server holds, as of the last reply. */
  saved: Answers
  /** How many changes by hand there have been. */
  edits = 0
  /** Per answer, the count its last change by hand was made at. */
  editedAt: Record<string, number> = {}
  /** The count the last SETTLED save was made at -- stored, or refused. */
  settledAt = 0

  constructor(initial: Answers) {
    this.form = { ...initial }
    this.saved = { ...initial }
  }

  /** A change made on the form, by hand. */
  edit(next: Answers): void {
    this.edits += 1
    for (const key of new Set([...Object.keys(this.form), ...Object.keys(next)])) {
      if ((this.form[key] ?? '') !== (next[key] ?? '')) this.editedAt[key] = this.edits
    }
    this.form = { ...next }
  }

  /** The count to stamp a save with, taken when the save is MADE -- a save
   *  queued behind a slow one goes out later, and an answer typed in that
   *  wait was not in it. */
  made(): number {
    return this.edits
  }

  /** What a save made from `next` should send now.
   *
   *  A key that fell out of the form is sent as "" rather than dropped: the
   *  server clears an answer it is handed empty, and a key simply left out
   *  means "leave it alone". And only keys changed by hand since the last
   *  save was settled, for the reason in the header. */
  body(next: Answers): Answers {
    const fresh = (key: string) => (this.editedAt[key] ?? 0) > this.settledAt
    const out: Answers = {}
    for (const [key, value] of Object.entries(next)) {
      if (this.saved[key] !== value && fresh(key)) out[key] = value
    }
    for (const key of Object.keys(this.saved)) {
      if (!(key in next) && fresh(key)) out[key] = ''
    }
    return out
  }

  /** The form after a save's outcome, BEFORE the caller prunes it against the
   *  tree: the server's view, except answers changed by hand since `madeAt`.
   *  `asked` is the set of keys the tree still asks; anything else is left
   *  out, since the server refuses a key its tree does not have. */
  merged(server: Answers, asked: ReadonlySet<string>, madeAt: number): Answers {
    const out: Answers = {}
    for (const key of new Set([...Object.keys(this.form), ...Object.keys(server)])) {
      if (!asked.has(key)) continue
      const value = (this.editedAt[key] ?? 0) > madeAt ? this.form[key] : server[key]
      if (value !== undefined && value.trim()) out[key] = value
    }
    return out
  }

  /** A save made at `madeAt` came back; `server` is what the server now holds. */
  stored(server: Answers, asked: ReadonlySet<string>, madeAt: number): Answers {
    this.saved = { ...server }
    this.settledAt = Math.max(this.settledAt, madeAt)
    return this.merged(server, asked, madeAt)
  }

  /** A save made at `madeAt` failed; the server holds what it held.
   *
   *  `refused` is whether the server JUDGED it -- a 4xx sentence saying no.
   *  Only then is it settled, so the next save does not send the same refusal
   *  again. A transport failure or a 5xx judged nothing, and settling it would
   *  lose the answer for good: the next save would leave it out, the reply
   *  would show the old value, and no error would remain to say so. */
  failed(asked: ReadonlySet<string>, madeAt: number, refused: boolean): Answers {
    if (refused) this.settledAt = Math.max(this.settledAt, madeAt)
    return this.merged(this.saved, asked, madeAt)
  }

  /** Draw `next` without counting it as a change by hand -- a reply or a
   *  rollback, already reconciled and pruned. */
  show(next: Answers): void {
    this.form = { ...next }
  }

  /** THE CALL'S NATURES CHANGED (076) and with them what is asked. `gone` are
   *  the keys no longer asked: forgotten here, so no save sends them -- the
   *  server refuses a key its tree does not have, and their answers stay on
   *  the ticket. `server` is what the re-read call holds for keys newly
   *  asked, adopted as stored so they are not mistaken for changes. */
  reshape(gone: readonly string[], server: Answers): void {
    for (const key of gone) {
      delete this.form[key]
      delete this.saved[key]
      delete this.editedAt[key]
    }
    Object.assign(this.saved, server)
    Object.assign(this.form, server)
  }
}

/** A sentence on the working call's panel, and WHAT IT IS ABOUT.
 *
 *  "Clear the message on the next successful save" lost the only explanation
 *  there was: leave the Year box at "201" and press a chip in the same motion,
 *  and the vehicle's save came back saying the year was left out -- then the
 *  CHIP's save came back a moment later with nothing to say and cleared it,
 *  while the dispatcher was looking at the chip. And "only a save made after
 *  the failure" left "Cannot reach the server" up after the queued save that
 *  delivered everything had succeeded.
 *
 *  So a message names the answers it concerns, and goes when THOSE answers are
 *  sent again and accepted. A `failure` -- a save refused, or not delivered --
 *  also goes when any save made after it succeeds: the dispatcher has moved on
 *  and the server is answering. A `note` -- something stored differently from
 *  what was sent -- stays until its own answer is given again, because it is a
 *  fact about the record and nothing else will say it. */
export type Notice = {
  text: string
  keys: string[]
  at: number
  kind: 'failure' | 'note'
}

/** The message still standing after a save made at `madeAt`, which sent
 *  `body`, succeeded. The caller then puts up anything this outcome itself
 *  had to say. */
export function afterSuccess(
  notice: Notice | null, body: Answers, madeAt: number,
): Notice | null {
  if (!notice) return null
  if (notice.keys.length > 0 && notice.keys.every((key) => key in body)) return null
  if (notice.kind === 'failure' && madeAt > notice.at) return null
  return notice
}
